import React from 'react';
import { useSelector } from 'react-redux';

import NotFoundPage from '../NotFoundPage/NotFoundPage';
import {
  resolveSeoSlug,
  buildSeoSearchParams,
  buildSeoCanonicalPath,
  renderSeoMetadata,
} from '../../util/seoLandingPages';

const buildSyntheticSearch = combo => {
  const seoSearchParams = buildSeoSearchParams(combo);
  return `?${new URLSearchParams(seoSearchParams).toString()}`;
};

/**
 * Renders the /sewa-:seoSlug SEO category+location landing pages (SOW
 * Sections 5 & 9). Deliberately a thin wrapper, not a parallel
 * implementation: it resolves the slug, then renders the SAME SearchPage
 * variant used at /s (passed in as `SearchPageComponent`, see the
 * `extraProps` on this route in src/routing/routeConfiguration.js) with
 * its `location.search` swapped for a synthetic query string carrying the
 * resolved category/location filters — the browser's address bar keeps
 * showing the clean /sewa-... path throughout, satisfying "clean URLs must
 * stay visible" while reusing 100% of SearchPage's existing rendering and
 * data-loading.
 *
 * An unresolved or currently ineligible slug renders the existing
 * NotFoundPage (SOW: "Unknown URLs return 404 instead of showing unrelated
 * search results"). server/resources/seoRedirects.js 301s away ineligible
 * paths that have an eligible fallback before the request gets this far;
 * this check covers the rest (no fallback, or not yet in the status table).
 */
const SeoLandingPage = props => {
  const { params, location, SearchPageComponent, ...rest } = props;
  const seoCategories = useSelector(state => state.seoPages?.seoCategories);
  const eligiblePaths = useSelector(state => state.seoPages?.eligiblePaths);
  const combo = resolveSeoSlug(params?.seoSlug, seoCategories || undefined);

  // Only render a page the daily eligibility table currently marks eligible.
  // Being known to the live taxonomy is not enough (SOW Sections 7 & 8): a
  // brand-new category/slug, or a page below threshold with no eligible
  // fallback, must 404.
  const isEligible = !!combo && (eligiblePaths || []).includes(buildSeoCanonicalPath(combo));

  if (!isEligible || !SearchPageComponent) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const { pageTitle, h1, metaDescription, metaKeywords } = renderSeoMetadata(combo);

  return (
    <SearchPageComponent
      {...rest}
      params={params}
      location={{ ...location, search: buildSyntheticSearch(combo) }}
      seoOverrides={{ title: pageTitle, h1, description: metaDescription, metaKeywords }}
    />
  );
};

export default SeoLandingPage;
